// Ввод: тапы и долгие нажатия по картам, перетаскивание, команды разметки (data-cmd) и меню.
// Импортируется ради побочного эффекта — регистрации слушателей на документе.
import type { BotLevel, CardRef, Game as GameState } from '../types.ts';
import { controlled, player, turnOf } from '../engine/state.ts';
import { restoreRuntime, recoverUndo, primeUndo, priceFor, ensureRulesVersion } from '../engine/game.ts';
import { NetClient } from './net.ts';
import { cardHtml } from './cards.ts';
import { needPid, layoutHand } from './board.ts';
import { sfx, unlock } from './sound.ts';
import { makeDragger } from './drag.ts';
import type { DropZone } from './drag.ts';
import { MIN_PLAYERS, MAX_PLAYERS } from './state.ts';
import { canInstall, requestInstall } from './install.ts';
import { loadGame, clearSave } from './storage.ts';
import * as OV from './overlays.ts';
import { readCmd } from './commands.ts';
import type { UiCommand, MenuItem } from './commands.ts';
import { byId, closest, inputValue, numData } from './dom.ts';
import { st, ui, $app, $, requireG, viewerId, toast, save, findCard, canAct, myPrompt, openModal, repaintModal, closeModal, modalOpen, currentModal } from './store.ts';
import { render, setRaised, unraise, setMode, repaintGame, showZoom, renderZoom, closeZoom, showLog } from './render.ts';
import { act, answer, affordableBuys, buyWithChips, startNewGame, quickBotsTable, duelTable, setupDoneForPlayer, showReview, startOnline, leaveOnline, toggleFullscreen, toggleMute } from './flow.ts';
import { joinRoom, invitePlayer, openInbox, openChangelog } from './social.ts';
import { loadLeaders } from './leaders.ts';
import { loadCorr, createCorr, joinCorr, openCorr, openCorrFromLink } from './corr.ts';
import { enablePush, disablePush, removePushDevice, testPush } from './push.ts';
import { loadDaily, startDaily, showDailyGame, loadMyBosses } from './daily.ts';
import { PICKABLE_MUTATORS } from '../engine/mutators.ts';
import { openProfile, profileTab, showGame, showLossPage, toggleAchAll, linkShow, linkEnter, linkRedeem, linkLeave, linkCancel } from './profile.ts';
import { openPlayer } from './player.ts';
import { replayCmd, replayAt } from './replay.ts';
import { clearInbox, removeItem, newsEnabled, setNewsEnabled, requestSysPermission, unreadCount } from './inbox.ts';

// ---------- перетаскивание ----------
const dragger = makeDragger({
  canDrag(el) {
    if (!st.G || st.G.phase !== 'play' || modalOpen() || !canAct()) return null;
    const zone = el.dataset['zone'];
    if (zone === 'hand') return { axis: 'any' };
    if (zone === 'market' && el.classList.contains('buyable')) return { axis: 'y' };
    return null;
  },
  zonesFor(el) {
    const zone = el.dataset['zone'];
    if (zone === 'hand') {
      // цели — противники сверху; всё остальное (стол) — просто сыграть. Себя целью выбирают уже в запросе.
      const zs: DropZone[] = [];
      for (const av of $app.querySelectorAll<HTMLElement>('.av[data-pl]')) {
        const pid = numData(av, 'pl');
        if (pid != null) zs.push({ el: av, kind: 'player', pid, pad: 4 });
      }
      const stage = $('.stage');
      if (stage) zs.push({ el: stage, kind: 'table', pad: 0 });
      return zs;
    }
    if (zone === 'market') {
      // покупка: одна зона — моя панель (с запасом вниз до края экрана, где рука)
      const me = $('.me');
      return me ? [{ el: me, kind: 'buy', pad: 0, padBottom: 400 }] : [];
    }
    return [];
  },
  onStart() {
    unraise();
    sfx('select');
  },
  onEnd() {
    if (st.pendingRender) render();
  },
  // карта из руки при перетаскивании выглядит так же, как будет лежать на столе (размер и полный текст)
  ghostFor(el) {
    if (el.dataset['zone'] !== 'hand') return null;
    const c = cardOfEl(el);
    if (c.kind !== 'inst') return null;
    const wrap = document.createElement('div');
    wrap.innerHTML = cardHtml({ inst: c.card }, { cls: 'sm', zone: 'table' });
    const g = wrap.firstElementChild;
    if (!(g instanceof HTMLElement)) return null;
    g.style.width = getComputedStyle(document.documentElement).getPropertyValue('--cw-table').trim() || '118px';
    return g;
  },
  onDrop(el, z) {
    const c = cardOfEl(el);
    if (z.kind === 'buy') {
      if (c.kind === 'pile') act({ name: 'buyChaos', args: [] });
      else if (c.kind === 'inst') buyWithChips(c.card);
      return;
    }
    if (c.kind !== 'inst') return;
    if (z.kind === 'player') ui.pendingTarget = { iid: c.card.iid, pid: z.pid };
    act({ name: 'playCard', args: [c.card.iid] });
  },
});
export const isDragging = (): boolean => dragger.isDragging();

// ---------- карты в разметке ----------
type CardEl = { kind: 'inst'; card: CardRef } | { kind: 'pile'; pile: 'chaos' } | { kind: 'cid'; cid: string } | { kind: 'none' };

/** Что за карта нарисована в элементе: экземпляр из состояния, стопка или просто описание */
function cardOfEl(el: HTMLElement): CardEl {
  if (el.dataset['pile'] === 'chaos') return { kind: 'pile', pile: 'chaos' };
  const iid = numData(el, 'iid');
  const cid = el.dataset['cid'];
  if (iid != null) {
    const c = findCard(iid) ?? (cid ? { iid, cid } : null);
    return c ? { kind: 'inst', card: c } : { kind: 'none' };
  }
  return cid ? { kind: 'cid', cid } : { kind: 'none' };
}

/** Тап по карте: смысл зависит от зоны */
function onCardTap(g: GameState, el: HTMLElement): void {
  const zone = el.dataset['zone'];
  const c = cardOfEl(el);
  const pr = myPrompt();
  switch (zone) {
    case 'hand':
      if (c.kind === 'inst') setRaised(c.card.iid);
      return;
    case 'token': {
      const tok = el.dataset['tok'] ?? '';
      openModal(() => OV.tokenCardV(g, tok, canAct()));
      return;
    }
    case 'freefam':
      if (c.kind === 'cid') {
        ui.setup.freeFam = c.cid;
        sfx('select');
        render();
      }
      return;
    case 'pick': {
      if (!pr || pr.type !== 'chooseCards' || c.kind !== 'inst') return;
      const iid = c.card.iid;
      if (ui.selected.has(iid)) ui.selected.delete(iid);
      else if (pr.max === 1) ui.selected = new Set([iid]);
      else if (ui.selected.size < pr.max) ui.selected.add(iid);
      else return;
      sfx('select');
      repaintGame();
      return;
    }
    case 'defend':
      if (pr && pr.type === 'defend' && c.kind === 'inst' && pr.cards.includes(c.card.iid)) {
        sfx('defend');
        answer(c.card.iid);
      }
      return;
    case 'market':
      if (c.kind === 'pile') showZoom(g, OV.chaosRef, 'chaos');
      else if (c.kind === 'inst') showZoom(g, c.card, 'market');
      return;
    case 'table':
      if (c.kind === 'inst') showZoom(g, c.card, controlled(g, player(g, turnOf(g).player)).some((x) => x.iid === c.card.iid) ? 'inplay' : 'view');
      return;
    default:
      if (c.kind === 'inst') showZoom(g, c.card, 'view');
      else if (c.kind === 'cid') showZoom(g, { iid: 0, cid: c.cid }, 'view');
  }
}

/** Долгое нажатие: карта крупно без действий */
function onCardLongPress(g: GameState, el: HTMLElement): void {
  const c = cardOfEl(el);
  if (c.kind === 'pile') showZoom(g, OV.chaosRef, 'chaos');
  else if (c.kind === 'inst') showZoom(g, c.card, el.dataset['zone'] === 'market' ? 'market' : 'view');
  else if (c.kind === 'cid') showZoom(g, { iid: 0, cid: c.cid }, 'view');
}

// ---------- слушатели документа ----------
let pressTimer: ReturnType<typeof setTimeout> | null = null;
let longPressed = false;

// картинка карты не загрузилась (нет арта офлайн) — прячем её, остаётся текстовая карта. Один слушатель на документ
// вместо inline-onerror: preact в живом DOM трактует «on…»-пропы как обработчики, строка там недопустима
document.addEventListener('error', (e) => { if (e.target instanceof HTMLImageElement) e.target.style.display = 'none'; }, true);
for (const evName of ['pointerup', 'click', 'keydown', 'touchend']) document.addEventListener(evName, () => unlock(), { passive: true });
// мышь → эффекты наведения (класс has-mouse); палец → выключить, чтобы hover не залипал на тач-экранах
document.addEventListener('pointermove', (e) => { document.documentElement.classList.toggle('has-mouse', e.pointerType === 'mouse'); }, { passive: true });
document.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') document.documentElement.classList.remove('has-mouse'); }, { passive: true });
document.addEventListener('pointerdown', (e) => {
  const cardEl = closest(e.target, '.card[data-cid]');
  if (!cardEl || !st.G) return;
  longPressed = false;
  pressTimer = setTimeout(() => {
    if (dragger.isDragging() || !st.G) return;
    longPressed = true;
    onCardLongPress(st.G, cardEl);
  }, 480);
}, { passive: true });
const cancelPress = (): void => {
  if (pressTimer) clearTimeout(pressTimer);
  pressTimer = null;
};
document.addEventListener('pointerup', cancelPress);
document.addEventListener('pointercancel', cancelPress);
document.addEventListener('pointermove', (e) => { if (pressTimer && (Math.abs(e.movementX) + Math.abs(e.movementY) > 4)) cancelPress(); });
document.addEventListener('contextmenu', (e) => { if (closest(e.target, '.card')) e.preventDefault(); });
window.addEventListener('resize', () => { if (st.G && st.G.phase === 'play' && $('#hand')) layoutHand($app); });

document.addEventListener('click', (e) => {
  if (longPressed) {
    longPressed = false;
    return;
  }
  const t = e.target;
  if (!(t instanceof Element)) return;
  // затемнение окна: клик мимо диалога закрывает
  const overlayEl = t.closest<HTMLElement>('[data-overlay]');
  if (overlayEl && t === overlayEl) {
    closeZoom();
    return;
  }
  // тап по карте — по её зоне; кнопка внутри карты (☀ Применить) — команда
  const cmdEl = t.closest<HTMLElement>('[data-cmd]');
  const cardEl = t.closest<HTMLElement>('.card[data-cid]');
  if (cardEl && !(cmdEl && cardEl.contains(cmdEl))) {
    if (st.G) onCardTap(st.G, cardEl);
    return;
  }
  const c = readCmd(t);
  if (!c) {
    if (ui.raised != null && st.G && !closest(t, '#preview')) setRaised(ui.raised);
    return;
  }
  runCommand(c);
});

function readNames(): void {
  for (const i of document.querySelectorAll<HTMLInputElement>('input[data-name]')) {
    const idx = numData(i, 'name');
    if (idx != null) ui.names[idx] = i.value.trim();
  }
}

// ---------- команды ----------
/** Исполнение команды разметки. Полный перебор: компилятор не даст забыть команду. */
function runCommand(c: UiCommand): void {
  switch (c.k) {
    // --- старт и лобби ---
    case 'rebalanceToggle':
      ui.rebalance = !ui.rebalance;
      try { localStorage.setItem('krutagidon-rebalance', ui.rebalance ? '1' : '0'); } catch { /* приватный режим */ }
      return;
    case 'instantToggle':
      ui.instantBots = !ui.instantBots;
      try { localStorage.setItem('krutagidon-instant-bots', ui.instantBots ? '1' : '0'); } catch { /* приватный режим */ }
      render();
      return;
    case 'npMinus': ui.nPlayers = Math.max(MIN_PLAYERS, ui.nPlayers - 1); render(); return;
    case 'npPlus': ui.nPlayers = Math.min(MAX_PLAYERS, ui.nPlayers + 1); render(); return;
    case 'newGame':
    case 'quickBots': {
      if (c.k === 'newGame') readNames();
      const quick = c.k === 'quickBots';
      const old = loadGame();
      if (old && old.G.phase !== 'ended') openModal(() => OV.replaceSavedV(old.G.players.map((p) => p.name), quick));
      else startNewGame(quick ? quickBotsTable() : undefined);
      return;
    }
    case 'quickN':
      ui.quickBots = Math.max(1, Math.min(MAX_PLAYERS - 1, c.n));
      try { localStorage.setItem('krutagidon-quick-n', String(ui.quickBots)); } catch { /* приватный режим */ }
      render();
      return;
    case 'quickBossToggle':
      ui.quickBoss = !ui.quickBoss;
      try { localStorage.setItem('krutagidon-quick-boss', ui.quickBoss ? '1' : '0'); } catch { /* приватный режим */ }
      render();
      return;
    case 'quickLevel':
      ui.quickLevel = c.level;
      try { localStorage.setItem('krutagidon-quick-level', c.level); } catch { /* приватный режим */ }
      render();
      return;
    case 'newConfirm': closeModal(); startNewGame(c.boss ? duelTable(c.boss) : c.quick ? quickBotsTable() : undefined); return;
    case 'duelStart': {
      const old = loadGame();
      if (old && old.G.phase !== 'ended') openModal(() => OV.replaceSavedV(old.G.players.map((p) => p.name), false, c.boss));
      else startNewGame(duelTable(c.boss));
      return;
    }
    case 'mutToggle':
      ui.mutators = ui.mutators.includes(c.id) ? ui.mutators.filter((m) => m !== c.id) : PICKABLE_MUTATORS.filter((m) => m === c.id || ui.mutators.includes(m));
      try { localStorage.setItem('krutagidon-mutators', JSON.stringify(ui.mutators)); } catch { /* приватный режим */ }
      render();
      return;
    case 'mutatorsHelp': openModal(() => OV.mutatorsHelpV()); return;
    case 'home':
      readNames();
      ui.home = c.tab;
      if (c.tab === 'top') ui.topTab = 'all';
      render();
      window.scrollTo(0, 0);
      if (c.tab === 'top') void loadLeaders(render);
      if (c.tab === 'daily') void loadDaily(render);
      if (c.tab === 'duel') void loadMyBosses(render);
      if (c.tab === 'corr') void loadCorr(render);
      return;
    case 'corrSize': ui.corrSize = c.n; render(); return;
    case 'corrCreate': readNames(); createCorr(ui.corrSize); return;
    case 'corrJoin': readNames(); joinCorr(c.code); return;
    case 'corrOpen': openCorr(c.code); return;
    case 'corrRefresh': void loadCorr(render); return;
    case 'corrGo': closeModal(); void openCorrFromLink(c.code, render); return;
    case 'pushEnable':
      void enablePush().then((ok) => {
        if (ok) toast('🔔 Уведомления включены');
        void loadCorr(render);
      });
      return;
    case 'pushDisable': void disablePush(); return;
    case 'pushRemove': void removePushDevice(c.id); return;
    case 'pushTest': void testPush().then((n) => { if (n) toast('Уведомление отправлено — должно появиться в шторке'); }); return;
    case 'topTab': ui.topTab = c.tab; render(); window.scrollTo(0, 0); return;
    case 'dailyStart': readNames(); void startDaily(); return;
    case 'dailyGame': showDailyGame(c.game, c.name, c.score, c.won); return;
    case 'mutatorInfo': if (st.G) openModal(() => OV.mutatorInfoV(requireG())); return;
    case 'endToDaily': {
      if (st.net) leaveOnline();
      ui.screen = 'start';
      ui.home = 'daily';
      ui.endShown = false;
      st.G = null;
      if (ui.botTimer) clearTimeout(ui.botTimer);
      ui.botTimer = null;
      render();
      void loadDaily(render);
      return;
    }
    case 'profile': openProfile(c.tab); return;
    case 'profileTab': profileTab(c.tab); return;
    case 'playerProfile': openPlayer(c.id); return;
    case 'playedGame': showGame(c.game, c.owner); return;
    case 'lossPage': showLossPage(c.owner, c.offset); return;
    case 'achAll': toggleAchAll(c.owner); return;
    case 'replay': replayCmd(c.op); return;
    case 'replayAt': replayAt(c.idx); return;
    case 'linkShow': linkShow(); return;
    case 'linkEnter': linkEnter(); return;
    case 'linkRedeem': linkRedeem(); return;
    case 'linkLeave': linkLeave(c.confirmed); return;
    case 'linkCancel': linkCancel(); return;
    // с экрана итога — в рейтинги, на номинацию только что сыгранной партии
    case 'endToTop': {
      const rc = st.rating.data;
      ui.topTab = rc ? (rc.cat === 'people' ? 'people' : rc.level ?? 'all') : 'all';
      if (st.net) leaveOnline();
      ui.screen = 'start';
      ui.endShown = false;
      st.G = null;
      if (ui.botTimer) clearTimeout(ui.botTimer);
      ui.botTimer = null;
      ui.home = 'top';
      render();
      window.scrollTo(0, 0);
      void loadLeaders(render);
      return;
    }
    case 'mute': toggleMute(); render(); return;
    case 'create': startOnline('create'); return;
    case 'join':
      ui.joinCode = inputValue('join-code').trim().toUpperCase();
      if (!ui.joinCode) toast('Введи код комнаты');
      else startOnline('join');
      return;
    case 'resume': startOnline('resume'); return;
    case 'forget': NetClient.forget(); render(); return;
    case 'leave': leaveOnline(); return;
    case 'start': st.net?.start(); return;
    case 'addBot': st.net?.addBot(c.level); return;
    case 'roomRebalance': st.net?.setRebalance(c.on); return;
    case 'roomOpen': st.net?.setOpen(c.on); return;
    case 'copy': navigator.clipboard.writeText(c.link).then(() => toast('Ссылка скопирована'), () => toast(c.link, 6000)); return;
    // --- матчмейкинг и уведомления ---
    case 'joinLobby': joinRoom(c.code); return;
    case 'invite': invitePlayer(c.id); return;
    case 'inbox': openInbox(); return;
    case 'changelog': openChangelog(); return;
    case 'inboxClear': clearInbox(); repaintModal(); return;
    case 'inboxRemove': removeItem(c.id); repaintModal(); return;
    case 'newsToggle': setNewsEnabled(!newsEnabled()); repaintModal(); return;
    case 'sysNotify': void requestSysPermission().then(() => repaintModal()); return;
    // человек → простой бот → сложный бот → человек
    case 'botToggle': {
      // цикл: человек → лёгкий → средний → сложный → нечестный → человек
      const next: Record<string, BotLevel | null> = { '': 'easy', easy: 'normal', normal: 'hard', hard: 'cheat', cheat: null };
      ui.bots[c.i] = next[ui.bots[c.i] ?? ''] ?? null;
      readNames(); render(); return;
    }
    case 'continue': {
      closeModal(); // «Продолжить старую» из вопроса о новой партии
      const s = loadGame();
      if (!s) return;
      // партия, начатая до появления версий правил: версия подбирается по журналу и запоминается — дальше
      // партия играется по своим правилам, а журнал воспроизводится (шаг назад)
      ensureRulesVersion(s.G);
      const g = restoreRuntime(s.G);
      // снимок для «шага назад» сохраняется вместе с партией; для партий, сохранённых старой версией,
      // остаётся запасной путь — вывести метку реплеем журнала
      if (s.undoBase) primeUndo(g, s.undoBase);
      else recoverUndo(g);
      st.G = g;
      ui.viewer = null;
      ui.screen = s.screen;
      ui.setup = s.setup;
      ui.seenSeq = s.seenSeq;
      ui.lastSeq = 0;
      // один человек за столом — передавать телефон некому
      if (g.phase === 'setup') ui.setup.stage = g.players.filter((p) => !p.bot).length > 1 ? 'pass' : 'choose';
      render();
      return;
    }
    case 'delete': clearSave(); render(); return;
    case 'toStart':
      ui.screen = 'start';
      ui.endShown = false;
      st.G = null;
      if (ui.botTimer) clearTimeout(ui.botTimer);
      ui.botTimer = null;
      render();
      return;
    // --- подготовка ---
    case 'setupPass': ui.setup.stage = 'choose'; sfx('turn'); render(); return;
    case 'trait': ui.setup.traitId = c.id; ui.setup.familiar = null; ui.setup.freeFam = null; sfx('select'); render(); return;
    case 'fam':
      ui.setup.familiar = c.cid;
      sfx('select');
      render();
      return;
    case 'setupDone': setupDoneForPlayer(); return;
    // --- передача телефона ---
    case 'pass': {
      const g = requireG();
      const need = needPid(g);
      ui.viewer = need;
      ui.lastSeq = g.eventSeq;
      ui.seenSeq[need] = g.eventSeq;
      ui.raised = null;
      ui.mode = 'play';
      sfx('turn');
      render();
      return;
    }
    case 'log': showLog(); return;
    case 'review': showReview(); return;
    // --- окна и меню ---
    case 'close': closeZoom(); return;
    case 'toggleOrig': ui.zoomOrig = !ui.zoomOrig; repaintModal(); return;
    case 'menuOpen':
      // метка отката могла не сохраниться (старая партия, другое устройство) — восстанавливаем её по журналу:
      // меню открывают редко, реплей здесь незаметен, зато кнопка не пропадает на ровном месте
      if (st.G && !st.net) recoverUndo(st.G);
      if (st.G) openModal(() => OV.menuV(requireG(), st.net ? (st.net.corr ? 'corr' : true) : false, canInstall(), viewerId(), unreadCount()));
      return;
    case 'menu': runMenu(c.item); return;
    case 'adjust':
      if (c.field === 'draw') act({ name: 'manualDraw', args: [c.pid, 1] });
      else act({ name: 'manualAdjust', args: [c.pid, c.field, c.delta] });
      if (!st.net && st.G) openModal(() => OV.manualV(requireG()));
      else closeModal();
      return;
    case 'goBuy': closeModal(); setMode('buy'); return;
    case 'endConfirm': closeModal(); act({ name: 'endTurn', args: [] }); return;
    // --- ответы на запросы ---
    case 'ans': answer(c.value); return;
    case 'opt': sfx('select'); answer(c.id); return;
    case 'pid': sfx('select'); answer(c.pid); return;
    case 'numStep': {
      const pr = myPrompt();
      if (!pr || pr.type !== 'chooseNumber') return;
      ui.numVal = Math.max(pr.min, Math.min(pr.max, (ui.numVal ?? pr.min) + c.d));
      repaintGame();
      return;
    }
    case 'numOk': {
      const pr = myPrompt();
      if (pr && pr.type === 'chooseNumber') answer(ui.numVal ?? pr.min);
      return;
    }
    case 'numPick': sfx('select'); answer(c.n); return;
    case 'cardsOk': answer([...ui.selected]); return;
    case 'sheetToggle': ui.sheetCollapsed = !ui.sheetCollapsed; repaintGame(); return;
    case 'cancel':
      ui.selected = new Set();
      ui.numVal = null;
      ui.pendingTarget = null;
      sfx('select');
      if (modalOpen()) closeModal(); // отмену можно нажать и из меню — оно должно закрыться
      act({ name: 'cancel', args: ['step'] }); // по шагам — и в партиях, начатых до v20
      return;
    case 'installNow':
      closeModal();
      void requestInstall().then((r) => {
        if (r === 'none') toast('Браузер не предлагает установку — открой игру в Chrome или Safari');
      });
      return;
    case 'installedFs':
      closeModal();
      if (!document.fullscreenElement) toggleFullscreen();
      return;
    // --- игровой экран ---
    case 'fs': toggleFullscreen(); return;
    case 'mode': setMode(c.mode); return;
    case 'tokens': if (st.G) openModal(() => OV.tokensV(player(requireG(), viewerId()))); return;
    case 'score': if (st.G) openModal(() => OV.scoreV(requireG(), player(requireG(), viewerId()))); return;
    case 'chipsInfo': if (st.G) openModal(() => OV.chipsInfoV(player(requireG(), viewerId()))); return;
    case 'rebalanceInfo': openModal(() => OV.rebalanceInfoV()); return;
    case 'deck': if (st.G) openModal(() => OV.pileV(player(requireG(), viewerId()), 'deck')); return;
    case 'discard': if (st.G) openModal(() => OV.pileV(player(requireG(), viewerId()), 'discard')); return;
    case 'trash': if (st.G) openModal(() => OV.trashV(requireG())); return;
    case 'handToggle': ui.handOpen = !ui.handOpen; render(); return;
    case 'endTurn': {
      const g = requireG();
      if (ui.autoEndTimer) clearTimeout(ui.autoEndTimer);
      ui.autoEndTimer = null;
      ui.autoEndAt = null;
      const buys = turnOf(g).power > 0 ? affordableBuys(g) : [];
      if (buys.length) openModal(() => OV.endTurnWarningV(turnOf(g).power, buys));
      else act({ name: 'endTurn', args: [] });
      return;
    }
    case 'play': closeZoom(); ui.raised = null; act({ name: 'playCard', args: [c.iid] }); return;
    case 'activate': closeZoom(); act({ name: 'activateCard', args: [c.iid] }); return;
    case 'activateToken': closeZoom(); act({ name: 'activateCard', args: [c.id] }); return;
    case 'buy': {
      const chips = ui.zoom?.useChips ?? 0;
      closeZoom();
      act({ name: 'buyCard', args: [c.iid, chips] });
      return;
    }
    case 'buyChaos': closeZoom(); act({ name: 'buyChaos', args: [] }); return;
    case 'bc': {
      const g = requireG();
      const z = ui.zoom;
      if (!z) return;
      const active = player(g, turnOf(g).player);
      const price = priceFor(g, active, z.card);
      z.useChips = Math.max(0, Math.min(Math.min(active.chips, price), z.useChips + c.d));
      renderZoom(g);
      return;
    }
    case 'zoom': {
      const card_ = findCard(c.iid);
      if (card_ && st.G) showZoom(st.G, card_, 'hand');
      return;
    }
    case 'zoomCid': {
      // значок карты в журнале/ленте/вопросе: полное описание поверх текущего окна, после закрытия — вернуть окно
      if (!st.G) {
        // вне партии (справка режима ребаланса на стартовом экране) — описание без игрового контекста
        const prev = currentModal();
        const cid = c.cid;
        ui.zoomOrig = false;
        openModal(() => OV.cardInfoV(cid, ui.zoomOrig));
        ui.zoomBack = prev ? () => openModal(prev) : null;
        return;
      }
      const prev = ui.zoom ? null : currentModal();
      const box = byId('logbox');
      const scroll = box ? box.scrollTop : 0;
      showZoom(st.G, { iid: 0, cid: c.cid }, 'view');
      if (prev) ui.zoomBack = () => {
        openModal(prev);
        const b = byId('logbox');
        if (b) b.scrollTop = scroll;
      };
      return;
    }
    case 'unraise': setRaised(ui.raised); return;
    case 'player': {
      requireG();
      const pr = myPrompt();
      // во время выбора цели тап по игроку — ответ
      if (pr && pr.type === 'choosePlayer' && pr.candidates.includes(c.pid)) {
        sfx('select');
        answer(c.pid);
        return;
      }
      const pid = c.pid;
      openModal(() => OV.playerInfoV(requireG(), player(requireG(), pid), pid === ui.viewer));
      return;
    }
  }
}

function runMenu(item: MenuItem): void {
  requireG();
  switch (item) {
    case 'log': showLog(); return;
    case 'tokens': openModal(() => OV.tokensV(player(requireG(), viewerId()))); return;
    case 'trash': openModal(() => OV.trashV(requireG())); return;
    case 'manual': openModal(() => OV.manualV(requireG())); return;
    case 'rules': openModal(() => OV.rulesV()); return;
    case 'mute': toggleMute(); openModal(() => OV.menuV(requireG(), st.net ? (st.net.corr ? 'corr' : true) : false, canInstall(), viewerId(), unreadCount())); return;
    case 'inbox': openInbox(); return;
    case 'changelog': openChangelog(); return;
    case 'fs': closeModal(); toggleFullscreen(); return;
    case 'install':
      closeModal();
      void requestInstall().then((r) => {
        if (r === 'ios') openModal(() => OV.installHintV('ios'));
        else if (r === 'chrome') openModal(() => OV.installHintV('chrome'));
        else if (r === 'none') toast('Браузер не предлагает установку — открой игру в Chrome или Safari');
      });
      return;
    case 'quit':
      save();
      ui.screen = 'start';
      closeModal();
      if (ui.botTimer) clearTimeout(ui.botTimer);
      ui.botTimer = null;
      render();
      return;
    case 'leave': leaveOnline(); return;
  }
}
